import Vue from "vue";
import Router, { RouteConfig } from "vue-router";

import { get } from "lodash";

import Login from "@/views/Login.vue";
import Logout from "@/components/Logout.vue";
import TempLogin from "@/views/TempLogin.vue";
import EventQueue from "@/views/EventQueue.vue";
import SiteMonitor from "@/views/SiteMonitor.vue";
import SiteMonitorCameras from "@/views/SiteMonitorCameras.vue";
import Cameras from "@/views/Cameras.vue";
import EventSearchPage from "@/views/EventSearchPage.vue";
import ActionSetup from "@/views/ActionSetup.vue";
import DispatchSetup from "@/views/DispatchSetup.vue";
import SiteAdminLanding from "@/views/SiteAdminLanding.vue";
import MobileCore from "@/components/mobile/core/MobileCore.vue";
import Views from "@/views/Views.vue";
import UserManagementSetup from "./views//UserSetup/UserManagementSetup.vue";
import Contacts from "./views/Contacts.vue";
import ContactRoles from "./views/ContactRoles.vue";
import Reports from "@/views/Reports.vue";
import ActivityLogManagement from "./views/ActivityLogManagement.vue";
import FeatureSetup from "@/views/FeatureSetup.vue";
import AreaSetup from "./views/AreaSetup.vue";
import TourSetup from "./views/TourSetup.vue";
import GuardTourSetup from "./views/GuardTourSetup.vue";
import Permissions from "./views/Permissions.vue";
import Signup from "./views/Signup.vue";
import AlarmSetup from "./views/AlarmSetup.vue";
import MobileRaiseSetup from "./views/MobileRaiseSetup.vue";
import LicenseDetails from "./views/LicenseDetails.vue";
import VideoReporting from "@/views/VideoReporting.vue";
import SyncSetup from "./views/SyncSetup.vue";
import Settings from "./views/Settings.vue";
import MapSetup from "./views/MapSetup.vue";
import ExternalMapLayerSetup from "./views/ExternalMapLayerSetup.vue";
import EventCategorySetup from "./views/EventCategorySetup.vue";
import ExternalMenuLinkSetup from "./views/ExternalMenuLinkSetup.vue";
import CCureLockdown from "./views/Lockdown.vue";
import { UserPermissions, FeaturesList } from "./store/types";
import store from "@/store";

Vue.use(Router);

export enum AppRoutes {
	QuickControl = "quick-control",
	EventConfigurationSetup = "event-configuration",
	EventQueue = "event-queue",
}

// NOTE meta.key should not be changed, this is unique.
export const routes: RouteConfigX[] = [
	{
		path: "/",
		name: "home",
		redirect: "login",
		meta: {
			title: "",
			auth: true,
			key: "c761a5b0-9136-4fb5-9522-9ba79893a90a",
			heartbeat: false,
			canLandingPage: false,
		},
	},
	{
		path: "/login",
		name: "login",
		component: Login,
		meta: {
			title: "",
			key: "7684d04d-1405-42cc-b492-b2ab0e0aa213",
			loadFeatures: false,
			heartbeat: false,
			canLandingPage: false,
		},
	},
	{
		path: "/loginNoRedirect",
		name: "login",
		component: Login,
		meta: {
			title: "",
			key: "7684d04d-1405-42cc-b492-b2ab0e0aa213",
			loadFeatures: false,
			heartbeat: false,
			canLandingPage: false,
		},
	},
	{
		path: "/Access/:sessionID?",
		name: "singleEventLogin",
		component: TempLogin,
		meta: {
			title: "",
			key: "fc4e4688-1a72-4abe-8f4c-7cce8539dd85",
			heartbeat: false,
			canLandingPage: false,
		},
	},
	{
		path: "/Lockdown",
		name: "lockdown",
		component: CCureLockdown,
		meta: {
			auth: true,
			title: "Lockdown",
			key: "419b5d8f-e16f-4462-ad9c-8889653141bf",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				(permissions.canViewCCureLockdown || permissions.canEditCCureLockdown) && get(featuresList, ["Integration", "CCure", "UI", "Lockdown"]),
			iconClass: "fas fa-door-closed",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/Views/Access/:sessionID",
		name: "singleEventLoginForViews",
		component: TempLogin,
		meta: {
			title: "",
			key: "d265357a-bba8-4bf2-bb29-a1794488dc4a",
			heartbeat: false,
			canLandingPage: false,
		},
	},
	{
		path: "/event-queue",
		name: AppRoutes.EventQueue,
		component: EventQueue,
		meta: {
			auth: true,
			title: "Alarm Processing",
			key: "a5552102-5745-489e-b53a-5cc82f160445",
			show: (permissions: UserPermissions) => permissions.canHandleAlarms,
			iconClass: "fas fa-bell",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/site-monitor/:eventId",
		name: "site-monitor",
		component: SiteMonitor,
		meta: {
			title: "",
			auth: true,
			key: "ed02b1d1-cc28-4b50-b2ab-a3ffc83a0924",
			canLandingPage: false,
		},
	},
	{
		path: "/site-monitor-cameras",
		name: "site-monitor-cameras",
		component: SiteMonitorCameras,
		meta: {
			title: "",
			auth: true,
			key: "0eb5a7ad-8fe5-4e07-97f1-d7105504e145",
			closeOnLogout: true,
			heartbeatRequired: false,
			canLandingPage: false,
		},
	},
	{
		path: "/quick-control",
		name: AppRoutes.QuickControl,
		component: () => import("@/views/QuickControl.vue"),
		meta: {
			title: "",
			auth: true,
			closeOnLogout: true,
			key: "fce92d74-22d7-4210-b858-40f14380a9de",
			heartbeatRequired: false,
			canLandingPage: false,
		},
	},
	{
		path: "/activity-log",
		name: "activity-log",
		component: ActivityLogManagement,
		meta: {
			auth: true,
			title: "Activity Log",
			key: "288bf0c2-3977-4795-be0d-87083d9cd9fe",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				permissions.canViewActivityLogManagement && get(featuresList, "ActivityLog"),
			iconClass: "fa fa-list-alt",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/Dispatch",
		name: "dispatch",
		component: () => import("@/views/Dispatch.vue"),
		meta: {
			auth: true,
			title: "Dispatch",
			key: "ab6e0c23-2a5b-4bec-b21e-afaf8608e911",
			show: (permissions, featuresList) =>
				permissions["canManageDispatch"] && get(featuresList, ["Dispatch"]),
			iconClass: "fas fa-walking",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/cameras",
		name: "cameras",
		component: Cameras,
		meta: {
			title: "",
			auth: true,
			key: "4544f576-08c4-4c9d-b6a5-a660bda3e32f",
			canLandingPage: false,
		},
	},
	{
		path: "/eventsearch/:eventId?",
		name: "eventsearch",
		component: EventSearchPage,
		meta: {
			auth: true,
			title: "Event Search",
			key: "57239380-4480-459d-8a81-d03ff8ef0915",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				permissions.canViewEvents && get(featuresList, ["Alarms", "EventSearch"]),
			iconClass: "fas fa-search",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/admin",
		name: "admin",
		component: SiteAdminLanding,
		meta: {
			title: "",
			auth: true,
			key: "0ade87fe-7d5b-4763-ba28-772b700b52ae",
			canLandingPage: false,
		},
	},
	{
		path: "/mobile",
		name: "mobile",
		component: MobileCore,
		meta: {
			title: "Mobile",
			auth: true,
			key: "662467c7-8d34-4545-9fce-c234184b39c5",
			redirectOnLogin: true,
			canLandingPage: true,
		},
	},
	{
		path: "/views",
		name: "views",
		component: Views,
		meta: {
			auth: true,
			redirectOnLogin: true,
			title: "Views",
			key: "b2299438-f0b8-4bc4-a4e2-3ed82f8d1c2b",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				permissions.canAccessVideowall && get(featuresList, "Views"),
			iconClass: "fas fa-border-all",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/views/:viewid/page/:pageNumber",
		name: "views",
		component: Views,
		meta: {
			auth: true,
			redirectOnLogin: true,
			title: "Views",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				permissions.canAccessVideowall && get(featuresList, "Views"),
			iconClass: "fas fa-border-all",
			canLandingPage: false,
			showInNav: false,
		},
	},
	{
		path: "/reports/",
		name: "reports",
		component: Reports,
		meta: {
			auth: true,
			title: "Insight",
			key: "7c05300a-883f-4f9b-93cc-b53866bc20ba",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				(permissions.canViewReports && get(featuresList, "Insights")),
			iconClass: "fas fa-chart-bar",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/reports/:reportType",
		name: "reports-loaded-report",
		component: Reports,
		meta: {
			title: "",
			key: "df319012-8ea7-4525-9925-efb17f32a6e1",
			auth: true,
			canLandingPage: false,
		},
	},
	{
		path: "/contacts/",
		name: "contacts",
		component: Contacts,
		meta: {
			auth: true,
			title: "Contacts",
			key: "8df7ef79-8231-4b7c-93aa-4b386609af87",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				(permissions.canEditSiteSetup || permissions.canViewSiteSetup) && get(featuresList, "Contacts"),
			iconClass: "fas fa-address-book",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/device-setup/",
		name: "device setup",
		component: () => import("@/views/DeviceSetup.vue"),
		meta: {
			auth: true,
			title: "Setup: Devices",
			key: "494e8555-e2d2-4bf2-bb8f-d425f8f8303b",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				(permissions.isAdmin || permissions.canEditSiteSetup) && get(featuresList, "Devices"),
			iconClass: "fas fa-power-off",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/contact-roles/",
		name: "contact-roles",
		component: ContactRoles,
		meta: {
			title: "Contact Roles",
			key: "712e7d64-b8e4-40f6-aa6d-5f05def2f697",
			auth: true,
			canLandingPage: false,
		},
	},
	{
		path: "/areas",
		name: "areas",
		component: AreaSetup,
		meta: {
			auth: true,
			title: "Areas",
			key: "013a0e70-8765-425d-86da-de4d2086f0bd",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				permissions.canViewSiteSetup && get(featuresList, "Areas"),
			iconClass: "fas fa-city",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/action-setup",
		name: "action-setup",
		component: ActionSetup,
		meta: {
			auth: true,
			title: "Setup: Actions",
			key: "0bf2509a-ba1f-4880-8429-d053b5b910da",
			show: (permissions: UserPermissions) => (permissions.canEditSettings ||
				permissions.canViewActionPlans ||
				permissions.canEditActionPlans),
			iconClass: "fas fa-clipboard-list-check",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/admin/dispatch-setup",
		name: "dispatch-setup",
		component: DispatchSetup,
		meta: {
			auth: true,
			title: "Setup: Dispatch",
			key: "bd87d921-81fa-40eb-bfbd-7f092cfe07e9",
			show: (permissions: UserPermissions) =>
				(permissions.canEditSettings ||
					permissions.canViewDispatchSetup ||
					permissions.canEditDispatchSetup),
			iconClass: "far fa-street-view",
			canLandingPage: true,
			showInNav: true,
		}
	},
	{
		path: "/event-configuration",
		name: AppRoutes.EventConfigurationSetup,
		component: () => import("@/views/EventConfigurationSetup.vue"),
		meta: {
			title: "Event Configuration",
			key: "1bae3334-281b-4134-b183-a24709aaa432",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				(permissions.canEditSettings && get(featuresList, "EventTypeSetup")),
			iconClass: "fas fa-cog",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/users/",
		name: "users",
		component: UserManagementSetup,
		meta: {
			auth: true,
			title: "Users",
			key: "f6485502-4f02-4fdf-a883-28c8adc4317b",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				(permissions.isAdmin ||
					permissions.canEditUserPermissions ||
					permissions.canEditSiteSetup ||
					permissions.canViewSiteSetup) &&
				get(featuresList, ["Users"]),
			iconClass: "fas fa-users",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/permissions/",
		name: "permissions",
		component: Permissions,
		meta: {
			auth: true,
			title: "Permissions",
			key: "e1bcb3ce-af9e-4913-8275-0e4c832edee6",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				(permissions.isAdmin || permissions.canEditUserGroups) && get(featuresList, "UserGroups"),
			iconClass: "fas fa-user-check",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/tours",
		name: "tours",
		component: TourSetup,
		meta: {
			auth: true,
			title: "Setup: Video Tour",
			key: "20d8ff1d-abb5-4ea7-9450-9e76824654f7",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				permissions.canEditSettings && get(featuresList, "VideoTours"),
			iconClass: "far fa-play-circle",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/guard-tours-setup",
		name: "guard-tours-setup",
		component: GuardTourSetup,
		meta: {
			auth: true,
			title: "Setup: Guard Tour",
			key: "761da1a6-f4f9-4dc5-8950-a52e3c794993",
			show: (permissions: UserPermissions, featuresList) =>
				(permissions.canEditGuardTours || permissions.canViewGuardTours && get(featuresList, ["Mobile", "GuardTours"])),
			iconClass: "fas fa-route",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/mobile-raise-setup",
		name: "mobile-raise-setup",
		component: MobileRaiseSetup,
		meta: {
			auth: true,
			title: "Setup: Mobile Raise",
			key: "62cc9d9f-ad79-47dd-8e1a-b884fc9b86ed",
			show: (permissions: UserPermissions, featuresList: FeaturesList) => {
				const userHasPermission = permissions.canViewMobileRaise || permissions.canEditMobileRaise;
				const featureEnabled = get(featuresList, ["Mobile", "MobileRaise"]);
				return featureEnabled && userHasPermission;
			},
			iconClass: "fas fa-mobile-alt",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/feature-setup/",
		name: "feature-setup",
		component: FeatureSetup,
		meta: {
			auth: true,
			title: "Setup: Features",
			key: "e9465a62-d492-41cb-916f-da8f75f5e877",
			show: (permissions: UserPermissions) => permissions.isAdmin,
			iconClass: "fas fa-cubes",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/signup/",
		name: "signup",
		component: Signup,
		meta: {
			title: "",
			key: "729f439f-df53-4bde-abc5-b65dd137f903",
			loadFeatures: false,
			canLandingPage: false,
		},
	},
	{
		path: "/license",
		name: "license",
		component: LicenseDetails,
		meta: {
			auth: true,
			title: "License",
			key: "e93702d2-d3f2-4036-a3bb-c714ed177d3e",
			show: (permissions: UserPermissions) => permissions.isAdmin || permissions.canViewLicenseDetails,
			iconClass: "fas fa-award",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/alarm-setup",
		name: "alarm-setup",
		component: AlarmSetup,
		meta: {
			auth: true,
			title: "Setup: Alarms",
			key: "22af3e61-37c4-4c44-bdf2-6641864fb974",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				(permissions.isAdmin || permissions.canViewSiteSetup) && get(featuresList, "AlarmSetup"),
			iconClass: "fas fa-bell-plus",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/video-reporting",
		name: "video-reporting",
		component: VideoReporting,
		meta: {
			auth: true,
			title: "Video Reporting",
			key: "ae2fc2a3-33a2-4322-b902-b14eb70ede8a",
			show: (permissions: UserPermissions, featuresList: FeaturesList) => {
				const hasPermission = permissions.canPerformVideoReport || permissions.canPerformVideoReportUpload;
				const hasFeature = get(featuresList, "VideoReporting");
				return hasPermission && hasFeature;
			},
			iconClass: "fas fa-file-video",
			openInNewTab: true,
			canLandingPage: false,
			showInNav: true,
		},
	},
	{
		path: "/sync-setup",
		name: "sync",
		component: SyncSetup,
		meta: {
			auth: true,
			title: "Sync",
			key: "158542c9-8997-43f0-8cfc-8cce39e159db",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				permissions.canEditSettings && get(featuresList, ["Sync"]),
			iconClass: "fas fa-sync",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/ExternalMapLayerSetup",
		name: "externalMapLayerSetup",
		component: ExternalMapLayerSetup,
		meta: {
			auth: true,
			title: "External Map Layers",
			key: "d6411be0-b79c-4e30-9f6b-33d2d4d8d239",
			show: (permissions, featuresList) =>
				(permissions.isAdmin ||
					permissions.canViewExternalMapLayers ||
					permissions.canEditExternalMapLayers) &&
				get(featuresList, ["ExternalMapLayers"]),
			iconClass: "fas fa-layer-group",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/settings",
		name: "settings",
		component: Settings,
		meta: {
			auth: true,
			title: "System Settings",
			key: "eba72616-8579-4c55-a901-db9329eb93f7",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				permissions.canEditSystemSettings && get(featuresList, ["Settings"]),
			iconClass: "fas fa-cogs",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/map-setup",
		name: "map-setup",
		component: MapSetup,
		meta: {
			auth: true,
			title: "Setup: Map",
			key: "9fb4ea01-b86d-45e0-826c-5987454a50f0",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				(permissions.isAdmin || permissions.canEditMaps) && get(featuresList, ["MapSetup"]),
			iconClass: "fas fa-map",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/EventCategorySetup",
		name: "eventCategorySetup",
		component: EventCategorySetup,
		meta: {
			auth: true,
			title: "Setup: Category",
			key: "49242ae2-ed0f-491c-8b4e-f882e4aba52a",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				permissions.canEditEventCategorySetup && get(featuresList, "EventCategorySetup"),
			iconClass: "far fa-object-ungroup",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/ExternalMenuLinkSetup",
		name: "externalMenuLinkSetup",
		component: ExternalMenuLinkSetup,
		meta: {
			auth: true,
			title: "Setup: Menu Link",
			key: "56063deb-19b2-4500-8f5e-0d55400b4fec",
			show: (permissions: UserPermissions, featuresList: FeaturesList) =>
				(permissions.canEditExternalMenuLinks || permissions.canViewExternalMenuLinks) &&
				get(featuresList, ["NavMenu", "ExternalMenuLinks"]),
			iconClass: "fas fa-external-link-alt",
			canLandingPage: true,
			showInNav: true,
		},
	},
	{
		path: "/logout/",
		name: "logout",
		component: Logout,
		meta: {
			title: "",
			canLandingPage: false,
			key: "a9b64e4c-7622-4820-933d-4c88db7aa68c",
		}
	},
	{
		// handle a non-existing route - redirect to root rotue
		path: "*",
		redirect: "/",
		meta: {
			title: "",
			canLandingPage: false
		}
	},
];

/**
 *
 * @returns a list of route names.
 */
export function getLandingPages(filterByPermissions: boolean = false, permissions: UserPermissions | undefined = undefined, sort: boolean = true): RouteConfigX[] {
	if (!permissions) {
		permissions = store.getters.getPermissions;
	}

	let routeList = routes.filter(x => x.meta?.canLandingPage && x.meta?.title && x.meta?.key)
	.filter(x => {
		if (!filterByPermissions) return true;

		return routeHasPermission(x, permissions);
	})
	.filter(x => x);

	if (sort) {
		routeList = routeList.sort((a, b) => a.meta.title.localeCompare(b.meta.title));
	}

	return routeList;
}

export const findRouteByKey = (key: string): RouteConfigX | null =>
	(routes.find(x => x.meta?.key == key)) ?? null;

export const routeHasPermission = (route: RouteConfigX, permissions: UserPermissions | undefined = undefined): boolean => {
	const featuresList: FeaturesList = store.getters.getFeaturesList;
	if (!route) return false;
	if (!route.meta?.show || typeof route.meta?.show !== "function") return true;
	return route.meta!.show(permissions, featuresList) ?? false;
}

export default new Router({
	routes,
});

// Interface to override RouteConfig to strongly type meta
export interface RouteConfigX extends Omit<RouteConfig, "metaData"> {
	meta?: RouteMetaData;
}

interface RouteMetaData {
	title: string;
	show?: (permissions?: UserPermissions, featuresList?: FeaturesList) => boolean | any;
	canLandingPage?: boolean;
	auth?: boolean;
	iconClass?: string;
	loadFeatures?: boolean;
	openInNewTab?: boolean;
	redirectOnLogin?: boolean;
	heartbeat?: boolean;
	closeOnLogout?: boolean;
	heartbeatRequired?: boolean;
	iconMask?: any;
	showInNav?: boolean;
	key?: string;
}
